/**
 * The dances (ท่าเต้น; packages/shared data/emotes `DANCE_LIST` has their names and loop lengths):
 * each a loop of key positions spread evenly round `FRAMES` frames, every frame part of the way from
 * one key to the next, eased so the weight settles into each beat instead of sliding at one pace.
 * `rigFor` (sprites.ts) asks `danceRig` for a dance pose's rig; the skeleton does the rest.
 *
 * A key sets the whole body: the drop (`o`), lean and pitch, both feet (stride and lift), both arms,
 * and the fabric — hair and sleeves trailing (`sway`), the hem lifted and flared (`hem`, `flare`).
 * Head-on the stride and lean vanish (they run toward the camera), so every dance also bobs, lifts
 * its feet and moves its arms, and reads from every side.
 */
import { EMOTES } from '@nine/shared';
import { type Ease, eased, FRAMES, type Pose, type Rig } from './sprites';

/** Pose names of the dances, the same as their emote ids. */
export const DANCE_POSES = [
  'danceFan',
  'danceSleeve',
  'danceLion',
  'danceDrunk',
  'danceSword',
  'danceStomp',
  'danceTwirl',
  'danceShuffle',
  'danceJig',
  'danceTaichi',
] as const;
export type DancePose = (typeof DANCE_POSES)[number];

export const isDance = (pose: Pose): pose is DancePose => (DANCE_POSES as readonly Pose[]).includes(pose);

/** One key position: numbers blend toward the next key, the arms and face switch at it. */
type Key = Partial<
  Pick<Rig, 'o' | 'lean' | 'tilt' | 'headDx' | 'f1' | 'f2' | 'l1' | 'l2' | 'sway' | 'hem' | 'flare'>
> &
  Pick<Rig, 'arm' | 'far'> &
  Partial<Pick<Rig, 'face' | 'closed'>>;

interface Dance {
  keys: readonly Key[];
  /** How each step between keys moves (the last back round to the first); `inOut` where none is given. */
  ease?: readonly Ease[];
}

const NUMS = ['o', 'lean', 'tilt', 'headDx', 'f1', 'f2', 'l1', 'l2', 'sway', 'hem', 'flare'] as const;

export const DANCES: Readonly<Record<DancePose, Dance>> = {
  // รำพัด: the fan hand traces a circle — out to the side, up to the face, over the head, down
  // low — while the weight rocks from the back foot to the front and the free hand answers it.
  danceFan: {
    keys: [
      { o: 1, lean: 1, tilt: 6, headDx: 1, f1: -4, f2: 3, sway: 1.2, hem: 1, arm: 'out', far: 'up' },
      { o: 0, tilt: -4, f1: -2, f2: 2, l2: 2, sway: 0.3, hem: 2, flare: 1, arm: 'raise', far: 'hip' },
      {
        o: 2,
        lean: -1,
        tilt: -8,
        headDx: -1,
        f1: -1,
        f2: 4,
        sway: -1.2,
        hem: 1,
        arm: 'overhead',
        far: 'fwd',
      },
      { o: 0, tilt: 2, f1: -3, f2: 2, l1: 2, sway: 0, hem: 2, flare: 1, arm: 'low', far: 'up' },
    ],
  },
  // รำแขนเสื้อ (水袖): long sleeves flung high, swept out, trailed along the ground and thrown back
  // up, the body arching back and bowing forward with them, the hem flaring all the while.
  danceSleeve: {
    keys: [
      { o: 0, tilt: -10, headDx: -1, f1: -2, f2: 2, sway: 2, hem: 2, flare: 2, arm: 'overhead', far: 'back' },
      { o: 1, lean: 1, tilt: -4, f1: -3, f2: 3, l2: 1, sway: 1, hem: 1, flare: 3, arm: 'out', far: 'up' },
      {
        o: 3,
        lean: 2,
        tilt: 12,
        headDx: 1,
        f1: -5,
        f2: 4,
        sway: -1,
        hem: 0,
        flare: 1,
        arm: 'low',
        far: 'up',
      },
      { o: 2, tilt: 4, f1: -3, f2: 2, l1: 2, sway: -2, hem: 2, flare: 2, arm: 'back', far: 'fwd' },
      {
        o: 0,
        lean: -1,
        tilt: -8,
        headDx: -1,
        f1: -2,
        f2: 3,
        sway: 1.5,
        hem: 3,
        flare: 3,
        arm: 'flail',
        far: 'up',
      },
      {
        o: 1,
        tilt: -2,
        f1: -2,
        f2: 1,
        l2: 1,
        sway: 0.5,
        hem: 1,
        flare: 1,
        arm: 'raise',
        far: 'hang',
        closed: true,
      },
    ],
  },
  // ก้าวเชิดสิงโต: sunk in a wide horse stance, both hands up as if holding the lion's head, shaking
  // it; the front knee lifts high and stamps down, then the back one, the head reared between.
  danceLion: {
    keys: [
      { o: 5, lean: 1, tilt: 10, f1: -7, f2: 6, sway: 1, hem: 0, arm: 'raise', far: 'up', face: 'atk' },
      {
        o: 3,
        lean: 2,
        tilt: 4,
        f1: -7,
        f2: 6,
        l2: 5,
        sway: 1.5,
        hem: 2,
        arm: 'forward',
        far: 'fwd',
        face: 'atk',
      },
      { o: 6, tilt: 12, headDx: 1, f1: -6, f2: 7, sway: -0.5, hem: 0, arm: 'raise', far: 'up', face: 'atk' },
      {
        o: 3,
        lean: -1,
        tilt: 2,
        headDx: -1,
        f1: -7,
        f2: 6,
        l1: 5,
        sway: -1.5,
        hem: 2,
        arm: 'overhead',
        far: 'up',
        face: 'atk',
      },
    ],
    // Up off the stamp, down into the next.
    ease: ['out', 'in', 'out', 'in'],
  },
  // โยกหมัดเมา: the drunken fist's sway — reeling forward with the cup raised, staggering a step,
  // leaning far back, hopping on one leg, dipping low, and catching the balance again.
  danceDrunk: {
    keys: [
      {
        o: 1,
        lean: 3,
        tilt: 14,
        headDx: 2,
        f1: -2,
        f2: 3,
        sway: 2,
        hem: 1,
        arm: 'raise',
        far: 'back',
        closed: true,
      },
      { o: 3, lean: 1, tilt: 4, headDx: 1, f1: -4, f2: 1, l1: 3, sway: 0.5, hem: 2, arm: 'low', far: 'fwd' },
      {
        o: 2,
        lean: -3,
        tilt: -14,
        headDx: -2,
        f1: -1,
        f2: 4,
        sway: -2,
        hem: 1,
        arm: 'out',
        far: 'up',
        closed: true,
      },
      {
        o: 1,
        lean: -1,
        tilt: -6,
        headDx: -1,
        f1: -3,
        f2: 2,
        l2: 3,
        sway: -1,
        hem: 2,
        arm: 'raise',
        far: 'hip',
      },
      {
        o: 4,
        lean: 2,
        tilt: 18,
        headDx: 1,
        f1: -5,
        f2: 5,
        sway: 1.5,
        hem: 1,
        arm: 'chest',
        far: 'back',
        closed: true,
      },
      { o: 1, tilt: 2, f1: -2, f2: 2, l1: 1, sway: 0, hem: 2, arm: 'flail', far: 'up', face: 'atk' },
    ],
  },
  // รำกระบี่มือเปล่า: a sword form with the sword-fingers (劍指) for a blade — the salute, drawn back,
  // a cut, low sweep, the crane on one leg, a thrust, overhead and low again, the other hand's
  // fingers raised behind the head as a swordsman's are.
  danceSword: {
    keys: [
      { o: 1, f1: -2, f2: 1, sway: 0, hem: 0, arm: 'chest', far: 'clasp' },
      { o: 3, lean: -1, tilt: -6, f1: -5, f2: 3, l2: 2, sway: -1, hem: 1, arm: 'windup', far: 'fwd' },
      { o: 4, lean: 2, tilt: 12, f1: -8, f2: 6, sway: 2, hem: 2, arm: 'cut', far: 'up', face: 'atk' },
      { o: 5, lean: 1, tilt: 16, f1: -8, f2: 6, sway: 1, hem: 1, arm: 'lowBack', far: 'up' },
      { o: 1, tilt: -4, f1: -3, f2: 2, l1: 5, sway: -1.5, hem: 3, flare: 2, arm: 'out', far: 'back' },
      { o: 3, lean: 2, tilt: 8, f1: -7, f2: 7, sway: 2, hem: 2, arm: 'thrust', far: 'up', face: 'atk' },
      { o: 2, tilt: -6, f1: -4, f2: 3, l2: 2, sway: 0, hem: 2, flare: 1, arm: 'overhead', far: 'fwd' },
      { o: 4, lean: 1, tilt: 10, f1: -6, f2: 5, sway: 1, hem: 1, arm: 'low', far: 'up' },
    ],
    ease: ['inOut', 'in', 'out', 'inOut', 'in', 'out', 'in', 'inOut'],
  },
  // ปรบมือกระทืบเท้า: a village festival beat — hands open with a knee up, clap and stamp; the other
  // knee up with both arms flung wide ("hey!"), clap and stamp, the hips rocking side to side.
  danceStomp: {
    keys: [
      {
        o: 0,
        lean: 1,
        tilt: -2,
        f1: -3,
        f2: 2,
        l1: 4,
        sway: 0.8,
        hem: 2,
        arm: 'clapOpen',
        far: 'clapOpen',
        face: 'atk',
      },
      { o: 2, tilt: 4, f1: -3, f2: 2, sway: -0.4, hem: 0, arm: 'clap', far: 'clap', face: 'atk' },
      {
        o: 0,
        lean: -1,
        tilt: -6,
        f1: -3,
        f2: 2,
        l2: 4,
        sway: -0.8,
        hem: 2,
        arm: 'out',
        far: 'up',
        face: 'atk',
      },
      { o: 2, tilt: 4, f1: -3, f2: 2, sway: 0.4, hem: 0, arm: 'clap', far: 'clap', face: 'atk' },
    ],
    // Lifted quickly off the beat, driven down onto it.
    ease: ['in', 'out', 'in', 'out'],
  },
  // หมุนตัว: a twirl on the toes — arms flung wide, wrapped in, swept behind and thrown overhead,
  // the hair whipping one way then the other and the skirt flaring out round it.
  danceTwirl: {
    keys: [
      { o: 0, tilt: -2, f1: -2, f2: 2, l1: 1, sway: 2.5, hem: 3, flare: 4, arm: 'out', far: 'up' },
      { o: 2, tilt: 2, f1: -1, f2: 1, l2: 2, sway: 1, hem: 2, flare: 2, arm: 'chest', far: 'fold' },
      { o: 0, tilt: -4, f1: -2, f2: 2, l1: 2, sway: -2.5, hem: 4, flare: 4, arm: 'back', far: 'fwd' },
      {
        o: 2,
        tilt: -8,
        headDx: -1,
        f1: -1,
        f2: 1,
        l2: 1,
        sway: -1,
        hem: 3,
        flare: 3,
        arm: 'overhead',
        far: 'up',
        closed: true,
      },
    ],
    ease: ['linear', 'linear', 'linear', 'linear'],
  },
  // สเต็ปซุกซน: a cheeky heel-and-toe shuffle, elbows pumping in turn, head bobbing, a little hop.
  danceShuffle: {
    keys: [
      {
        o: 1,
        lean: 1,
        tilt: 4,
        headDx: 1,
        f1: -4,
        f2: 3,
        l2: 2,
        sway: 1,
        hem: 1,
        arm: 'guard',
        far: 'back',
        face: 'atk',
      },
      { o: 2, tilt: 2, f1: -2, f2: 1, sway: 0, hem: 0, arm: 'low', far: 'fwd', face: 'atk' },
      {
        o: 1,
        lean: -1,
        tilt: -2,
        headDx: -1,
        f1: -3,
        f2: 4,
        l1: 2,
        sway: -1,
        hem: 1,
        arm: 'guard',
        far: 'back',
        face: 'atk',
      },
      { o: 0, tilt: 0, f1: -2, f2: 1, l1: 1, l2: 1, sway: 0.5, hem: 2, arm: 'low', far: 'fwd', face: 'atk' },
    ],
  },
  // เต้นฉลองชัย: a victory jig — fist pumped high on each hop and swung out wide on each landing,
  // kicking one heel back and the other foot out in front, the free hand on the hip.
  danceJig: {
    keys: [
      {
        o: 0,
        tilt: -6,
        headDx: -1,
        f1: -3,
        f2: 2,
        l1: 4,
        sway: 0,
        hem: 2,
        arm: 'overhead',
        far: 'hip',
        face: 'atk',
      },
      { o: 2, tilt: 2, f1: -2, f2: 2, sway: 1, hem: 0, arm: 'out', far: 'hip', face: 'atk' },
      {
        o: 0,
        tilt: -6,
        headDx: -1,
        f1: -2,
        f2: 4,
        l2: 4,
        sway: 0,
        hem: 2,
        arm: 'overhead',
        far: 'hip',
        face: 'atk',
      },
      { o: 2, tilt: 2, f1: -2, f2: 2, sway: -1, hem: 0, arm: 'out', far: 'hip', face: 'atk' },
    ],
    ease: ['in', 'out', 'in', 'out'],
  },
  // รำไทเก๊ก: slow tai chi — ward off, single whip opening, the white crane on one toe, brush knee
  // sinking low, the pushing palm, and gathering back — the weight passing from leg to leg.
  danceTaichi: {
    keys: [
      { o: 3, tilt: 2, f1: -5, f2: 4, sway: 0.3, hem: 0, arm: 'guard', far: 'fwd' },
      { o: 4, lean: 1, tilt: 4, f1: -5, f2: 4, sway: 0.6, hem: 1, arm: 'out', far: 'fwd' },
      {
        o: 2,
        lean: -1,
        tilt: -2,
        f1: -3,
        f2: 2,
        l2: 2,
        sway: 0,
        hem: 1,
        flare: 1,
        arm: 'raise',
        far: 'back',
      },
      { o: 5, lean: 1, tilt: 8, f1: -6, f2: 5, sway: -0.4, hem: 0, arm: 'low', far: 'up' },
      { o: 4, lean: 2, tilt: 6, f1: -6, f2: 5, sway: -0.6, hem: 1, arm: 'thrust', far: 'hip' },
      { o: 3, f1: -4, f2: 3, l1: 1, sway: 0, hem: 1, arm: 'chest', far: 'fwd', closed: true },
    ],
  },
};

/** Seconds one round of a dance takes (shared data/emotes). */
export function danceSeconds(pose: DancePose): number {
  return EMOTES[pose]?.seconds ?? 1;
}

/**
 * A dance's rig at frame `ph` of `FRAMES`: the two keys either side, numbers blended by the eased
 * step, the weapon-side arm on its way between theirs (`armTo`), the free arm and face as the nearer
 * key has them.
 */
export function danceRig(pose: DancePose, ph: number): Partial<Rig> {
  const { keys, ease = [] } = DANCES[pose];
  const u = ((((ph % FRAMES) + FRAMES) % FRAMES) / FRAMES) * keys.length;
  const i = Math.floor(u);
  const a = keys[i]!;
  const b = keys[(i + 1) % keys.length]!;
  const t = eased(ease[i] ?? 'inOut', u - i);
  const out: Partial<Rig> = {};
  for (const n of NUMS) out[n] = (a[n] ?? 0) + ((b[n] ?? 0) - (a[n] ?? 0)) * t;
  out.arm = a.arm;
  if (b.arm !== a.arm && t > 0) {
    out.armTo = b.arm;
    out.armT = t;
  }
  const near = t < 0.5 ? a : b;
  out.far = near.far;
  out.face = near.face ?? 'idle';
  out.closed = near.closed ?? false;
  return out;
}
